import { Blob, NS3451, OMUK } from '@consigli/types';
import { DragStartEvent } from '@dnd-kit/core';
import { toast } from 'react-toastify';

import { FolderDocumentsType } from '@/util/types';

import { generateToastMessage } from './toast-utils';
import { FolderStructureStandard, mappingDict } from '../util/foldername-mappings';

export interface UpdateBlobParams {
  operation: 'copy' | 'move';
  selectedFolderForDrag: string;
  currentDraggedFile?: Blob;
  activeDraggedFile?: Blob | null;
  activeFolder: { id: string } | null;
  selectedFiles: FolderDocumentsType[];
  draggingCardId: string;
  projectId: number;
  updateBlob: (params: {
    projectId: number;
    packageId: string;
    blobId: string;
    data: Partial<Blob>;
  }) => Promise<unknown>;
  t: (key: string, params?: Record<string, unknown>) => string;
  onDragStart: (event: DragStartEvent | null) => void;
  setConfirmationPopupIsOpen: (open: boolean) => void;
}

export const handleUpdateBlobs = async ({
  operation,
  selectedFolderForDrag,
  currentDraggedFile,
  activeDraggedFile,
  activeFolder,
  selectedFiles,
  draggingCardId,
  projectId,
  updateBlob,
  t,
  onDragStart,
  setConfirmationPopupIsOpen,
}: UpdateBlobParams) => {
  const isMultiple = selectedFiles
    .map((item: FolderDocumentsType) => item.id)
    .includes(draggingCardId);

  // Determine which enum to use based on the selectedFolderForDrag value
  let targetFolder: FolderStructureStandard;

  if (selectedFolderForDrag in NS3451) {
    targetFolder = NS3451[selectedFolderForDrag as keyof typeof NS3451];
  } else if (selectedFolderForDrag in OMUK) {
    targetFolder = OMUK[selectedFolderForDrag as keyof typeof OMUK];
  } else {
    console.error('Invalid folder ID:', selectedFolderForDrag);
    toast.error(t('folders.could-not-move-file'));
    setConfirmationPopupIsOpen(false);
    return;
  }

  // Validate that we got a valid folder
  if (!targetFolder) {
    console.error('Could not resolve folder ID:', selectedFolderForDrag);
    toast.error(t('folders.could-not-move-file'));
    setConfirmationPopupIsOpen(false);
    return;
  }

  let data: { folderStructure: FolderStructureStandard[] } = {
    folderStructure: [targetFolder],
  };

  const filesToUpdate = isMultiple
    ? selectedFiles
    : currentDraggedFile
      ? [currentDraggedFile]
      : activeDraggedFile
        ? [activeDraggedFile]
        : [];

  if (filesToUpdate.length === 0) {
    setConfirmationPopupIsOpen(false);
    return;
  }

  const updatePromises = filesToUpdate.map((file) => {
    if (selectedFolderForDrag !== NS3451.UNCATEGORIZED) {
      const standardToAdd = data.folderStructure.filter(
        (value: FolderStructureStandard) => !file.folderStructure.includes(value),
      );

      let standardToRemove: FolderStructureStandard[] = [];
      let updatedFolderStructure: typeof file.folderStructure = [];

      if (operation === 'move') {
        // Remove from current folder, add to new folder
        standardToRemove = activeFolder ? [activeFolder.id as FolderStructureStandard] : [];
        updatedFolderStructure = standardToAdd;
      } else {
        // Keep existing folders, add new ones
        updatedFolderStructure = file.folderStructure
          .filter((value) => !standardToRemove.includes(value))
          .concat(standardToAdd);
      }

      data = { folderStructure: updatedFolderStructure };
    }

    const updateParams = {
      projectId,
      packageId: filesToUpdate[0].documentPackage,
      blobId: file.id,
      data,
    };

    return updateBlob(updateParams);
  });

  try {
    const results = await Promise.allSettled(updatePromises);

    // Check if any updates failed
    const failedUpdates = results.filter((result) => result.status === 'rejected');
    const successfulUpdates = results.filter((result) => result.status === 'fulfilled');

    if (failedUpdates.length > 0) {
      console.error('Some blob updates failed:', failedUpdates);
      failedUpdates.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Failed update ${index}:`, result.reason);
        }
      });
      toast.error(t('folders.could-not-move-file'));
      setConfirmationPopupIsOpen(false);
      return;
    }

    if (successfulUpdates.length > 0) {
      onDragStart(null);

      const folderName = mappingDict[selectedFolderForDrag as keyof typeof mappingDict];

      const toastMessage = generateToastMessage(
        t,
        operation,
        isMultiple,
        folderName as unknown as string,
        filesToUpdate,
      );

      toast.success(toastMessage);
    }
  } catch (error) {
    console.error('Error in handleUpdateBlobs:', error);
    toast.error(t('folders.could-not-move-file'));
  } finally {
    setConfirmationPopupIsOpen(false);
  }
};
